import { FormEvent, FormEventHandler, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form as BeamForm, Checkbox, ConventionalNativeSelect } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectLocale } from '@app/redux/locale';
import { EditableCardType, fetchScrubbedAddress, ScrubAddressModalType } from '@app/redux/scrubbedAddress';
import { selectUserInfo } from '@app/redux/userInfo';

import { AddressInput, Locale } from '@mfe/shared/schema-types';

import { AddressValidationComponent } from './AddressValidationComponent';
import EditableFooter from './EditableFooter';
import {
  createAddressForValidation,
  findCountryCode,
  getCountriesForNativeSelect,
  updateAddressHandler,
} from '../../utils';
import { ProfileTab } from '../../types';

import styles from '../styles.module.scss';

type EditableAddressProps = {
  currentTab: ProfileTab;
  type: EditableCardType.Billing | EditableCardType.Shipping;
  setRestrictSwitchTabs: (restrict: boolean) => void;
  clearOnClick: (type: EditableCardType) => void;
  cancelOnClick: (cancel: boolean) => void;
  setShowModal: (type: ScrubAddressModalType | null) => void;
};

export enum AddressCheckboxType {
  Billing = 'Billing',
  Shipping = 'Shipping',
}

const EditableAddress = ({
  type,
  setRestrictSwitchTabs,
  clearOnClick,
  cancelOnClick,
  setShowModal,
}: EditableAddressProps): JSX.Element => {
  const countryRef = useRef<HTMLSelectElement | null>(null);
  const { t } = useTranslation('Profile');
  const { userInfo } = useSelector(selectUserInfo);
  const { billing: currentBillingAddress, shipping: currentShippingAddress } = userInfo.address;
  const dispatch = useDispatch();
  const accountType = userInfo.accountType;

  const {
    locale: { userLocale, displayLanguage },
  } = useSelector(selectLocale);
  const isBrazil = userLocale === Locale.PtBr;

  const [isBillingCheckboxClicked, setBillingCheckbox] = useState<boolean>(false);
  const [isShippingCheckboxClicked, setShippingCheckbox] = useState<boolean>(false);

  const toggleBillingCheckbox = () => setBillingCheckbox(!isBillingCheckboxClicked);
  const toggleShippingCheckbox = () => setShippingCheckbox(!isShippingCheckboxClicked);

  const errorArray: string[] = [
    t('error_streetAddressInput'),
    t('error_streetAddress2Input'),
    t('error_cityInput'),
    t('error_stateProvinceInput'),
    t('error_zipcodeInput'),
  ];

  const countriesForNativeSelect = getCountriesForNativeSelect(displayLanguage).filter(
    (country: { label: string }) => country.label
  );

  const matchAddress = isBillingCheckboxClicked
    ? createAddressForValidation(currentShippingAddress)
    : createAddressForValidation(currentBillingAddress);

  const addressValidation = AddressValidationComponent({
    currentAddress:
      type === EditableCardType.Billing
        ? createAddressForValidation(currentBillingAddress)
        : createAddressForValidation(currentShippingAddress),
    addressType: type,
    disabledInputs: type === EditableCardType.Billing ? isBillingCheckboxClicked : isShippingCheckboxClicked,
    errorArray,
    matchAddress,
  });

  const resetFields = () => {
    setBillingCheckbox(false);
    setShippingCheckbox(false);
    setRestrictSwitchTabs(false);
    clearOnClick(EditableCardType.None);
    cancelOnClick(false);
  };

  const cancelClick = () => {
    resetFields();
  };

  const saveOnClick = () => {
    if (isBillingCheckboxClicked || isShippingCheckboxClicked) {
      const checkbox: AddressCheckboxType = isBillingCheckboxClicked
        ? AddressCheckboxType.Billing
        : AddressCheckboxType.Shipping;
      updateAddressHandler(accountType, checkbox, resetFields, dispatch, currentBillingAddress, currentShippingAddress);
    }

    if (addressValidation.isValidAddress && !isBillingCheckboxClicked && !isShippingCheckboxClicked) {
      const savedAddress = addressValidation.newAddress;
      const preScrubbedAddress: Omit<AddressInput, 'labels'> = {
        addressLines: savedAddress.addressLines,
        countryCode: findCountryCode(changeCountry.label),
        municipality: savedAddress.municipality,
        postalCode: savedAddress.postalCode,
        region: savedAddress.region,
      };
      dispatch(fetchScrubbedAddress(preScrubbedAddress));
      setShowModal(ScrubAddressModalType.Address);
    }
  };

  const defaultCountryObj = getDefaultCountryObj(userLocale, countriesForNativeSelect);

  const [changeCountry, setChangeCountry] = useState<{
    label: string;
    value: string;
  }>(defaultCountryObj);

  const changeCountryOnClick = () => {
    if (!countryRef?.current?.value) {
      return;
    }

    const newCountry = countriesForNativeSelect.find(
      (country: { value: string }) => country.value === countryRef?.current?.value
    );

    setChangeCountry({
      label: newCountry?.label ?? '',
      value: newCountry?.value ?? '',
    });
  };

  const disabled =
    !isBillingCheckboxClicked &&
    !isShippingCheckboxClicked &&
    (!addressValidation.isValidAddress || addressValidation.disableSaveButton);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveOnClick();
  };

  return (
    <BeamForm
      id="edit-address-form"
      data-cy="edit-address-form"
      name="edit-address-form"
      validationMode="onSubmit"
      onSubmit={handleSubmit}
    >
      <div className={styles['editableCard']}>
        {type === EditableCardType.Billing ? (
          !isBrazil && (
            <Checkbox
              id="sameAddressBilling"
              label={t('sameAsShippingAddress')}
              mb="24px"
              checked={isBillingCheckboxClicked}
              onClick={toggleBillingCheckbox}
            />
          )
        ) : (
          <Checkbox
            id="sameAddressShipping"
            label={t('sameAsBillingAddress')}
            mb="24px"
            checked={isShippingCheckboxClicked}
            onClick={toggleShippingCheckbox}
          />
        )}
        <ConventionalNativeSelect
          ref={countryRef}
          id="country"
          labelProps={{ labelText: t('Address.country') }}
          fluid={true}
          value={changeCountry.value}
          onChange={() => changeCountryOnClick()}
          options={countriesForNativeSelect}
          mb="24px"
        />
        {addressValidation.newElements}
        <EditableFooter disabled={disabled} cancelOnClick={cancelClick} saveOnClick={saveOnClick} />
      </div>
    </BeamForm>
  );
};

function getDefaultCountryObj(
  locale: Locale,
  countries: { label: string; value: string }[]
): { label: string; value: string } {
  const emptyState = { label: '', value: '' };
  switch (locale) {
    case Locale.EsMx:
      return countries.find((el) => el.label === 'Mexico') ?? emptyState;
    case Locale.PtBr:
      return countries.find((el) => el.label === 'Brazil') ?? emptyState;
    case Locale.ItIt:
      return countries.find((el) => el.label === 'Italy') ?? emptyState;
    case Locale.EnUs:
    default:
      return countries.find((el) => el.label === 'United States') ?? emptyState;
  }
}

export default EditableAddress;
